import { useTranslation } from 'react-i18next';
import { env } from '@/lib/env';

const SITE_ORIGIN = env.VITE_SITE_URL.replace(/\/+$/, '');

// `t` is typed against the literal key union of the `seo` namespace, but
// `RouteHead` accepts arbitrary key strings via props. Loosen locally so the
// component stays typed at the API boundary while preserving safety elsewhere.
type LooseT = (key: string) => string;

interface RouteHeadProps {
  /** Route path under the site origin, e.g. ROUTES.home (`/`). */
  path: string;
  /** i18n key for `<title>`. Convention: `seo:<page>.title`. */
  titleKey: string;
  /** i18n key for `<meta name="description">`. Convention: `seo:<page>.description`. */
  descriptionKey: string;
  /**
   * Optional i18n key for a shorter OG/Twitter description.
   * Falls back to `descriptionKey` when omitted.
   */
  ogDescriptionKey?: string;
  /** When true, emit `<meta name="robots" content="noindex,nofollow">`. */
  noindex?: boolean;
}

export function RouteHead({
  path,
  titleKey,
  descriptionKey,
  ogDescriptionKey,
  noindex,
}: RouteHeadProps) {
  const { t } = useTranslation('seo');
  const tk = t as unknown as LooseT;

  const title = tk(titleKey);
  const description = tk(descriptionKey);
  const ogDescription = ogDescriptionKey ? tk(ogDescriptionKey) : description;
  const siteName = t('site.name');
  const imagePath = t('site.default_image');
  const imageType = t('site.default_image_type');
  const imageWidth = t('site.default_image_width');
  const imageHeight = t('site.default_image_height');

  const canonical = `${SITE_ORIGIN}${path}`;
  const imageUrl = `${SITE_ORIGIN}${imagePath}`;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:type" content={imageType} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={imageUrl} />
    </>
  );
}
